import { useEffect, useRef, useState } from 'react';
import InlineConfirm from '../../components/InlineConfirm';
import { ServerSetting, setServerSetting } from '../../services/adminServerSettings';
import { errorMessage } from './adminErrors';

/** A chip that holds a sentence, which wraps instead of keeping the chip's one-line height. */
export const SentenceChipStyle = { height: 'auto', minHeight: 26, padding: '6px 11px', whiteSpace: 'normal', lineHeight: 1.35 } as const;

export interface SettingChoice {
  value: string;
  label: string;
}

const OnOffOptions: SettingChoice[] = [
  { value: 'false', label: 'Off' },
  { value: 'true', label: 'On' },
];

const ControlHeight = 36;

interface SettingRowProps {
  setting: ServerSetting;
  label: string;
  description?: string;
  /** Off or On; a setting with no stored value is Off. */
  onOff?: boolean;
  /** The values offered in a list instead of a text field; a setting with no stored value shows its default. */
  choices?: SettingChoice[];
  /** A line under the control, such as what bounds the choices. */
  note?: string;
  onSaved: (saved: ServerSetting) => void;
  /** The value in the control, unsaved or not, at its first render and at each change. */
  onDraft?: (draft: string) => void;
}

function refusal(valueError: string, setting: ServerSetting, onOff: boolean): string {
  const inItsPlace = onOff
    ? 'The default Off applies.'
    : setting.defaultValue
      ? `The default ${setting.defaultValue} applies.`
      : setting.defaultValue === ''
        ? 'It counts as not set.'
        : '';
  return `Refused: ${valueError.replace(/\.?$/, '.')} ${inItsPlace}`.trimEnd();
}

function NotStoredChip({ setting, onOff }: { setting: ServerSetting; onOff: boolean }) {
  const defaultValue = onOff ? 'Off' : setting.defaultValue;
  return defaultValue ? (
    <span className="chip"><span className="dot" />Default: {defaultValue}</span>
  ) : (
    <span className="chip warn"><span className="dot" />Not set</span>
  );
}

function initialDraft(setting: ServerSetting, onOff: boolean, choices: SettingChoice[] | undefined): string {
  if (setting.value != null) return setting.value;
  if (onOff) return 'false';
  return choices ? setting.defaultValue ?? '' : '';
}

interface ControlProps {
  label: string;
  draft: string;
  placeholder: string;
  options: SettingChoice[] | null;
  segmented: boolean;
  onChoose: (next: string) => void;
}

function SettingControl({ label, draft, placeholder, options, segmented, onChoose }: ControlProps) {
  if (segmented && options) {
    return (
      <div className="seg" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={draft === option.value}
            className={'seg-item' + (draft === option.value ? ' on' : '')}
            onClick={() => onChoose(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  }
  if (options) {
    const shown = options.some((option) => option.value === draft) ? options : [{ value: draft, label: draft }, ...options];
    return (
      <select
        className="select"
        value={draft}
        onChange={(e) => onChoose(e.target.value)}
        aria-label={label}
        style={{ height: ControlHeight, width: 'auto', minWidth: 96, padding: '0 12px' }}
      >
        {shown.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      className="input"
      value={draft}
      onChange={(e) => onChoose(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      style={{ height: ControlHeight, maxWidth: 220, padding: '0 12px' }}
    />
  );
}

export default function SettingRow({ setting, label, description, onOff = false, choices, note, onSaved, onDraft }: SettingRowProps) {
  const initial = initialDraft(setting, onOff, choices);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const reportDraft = useRef(onDraft);
  reportDraft.current = onDraft;

  useEffect(() => setDraft(initial), [initial]);
  useEffect(() => reportDraft.current?.(draft), [draft]);

  const options = onOff ? OnOffOptions : choices ?? null;
  const value = draft.trim();
  const changed = value !== initial;
  const valueLabel = options ? options.find((option) => option.value === value)?.label ?? value : `"${value}"`;
  const choose = (next: string) => {
    setDraft(next);
    setSaved(false);
    setConfirming(false);
  };

  const save = async () => {
    setConfirming(false);
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      onSaved(await setServerSetting(setting.key, value));
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not save the setting.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
        <span className="mono muted" style={{ fontSize: 11.5 }}>{setting.key}</span>
        {setting.value == null && <NotStoredChip setting={setting} onOff={onOff} />}
        {setting.valueError != null && (
          <span className="chip warn" style={SentenceChipStyle}>
            <span className="dot" style={{ flexShrink: 0 }} />
            {refusal(setting.valueError, setting, onOff)}
          </span>
        )}
      </div>
      {description && <p className="muted-2" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.45 }}>{description}</p>}
      <form
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (changed && value) setConfirming(true);
        }}
      >
        <SettingControl
          label={label}
          draft={draft}
          placeholder={setting.defaultValue || 'Not set'}
          options={options}
          segmented={onOff}
          onChoose={choose}
        />
        {confirming ? (
          <InlineConfirm
            question={`Set ${label} to ${valueLabel}? The change applies right away.`}
            confirmLabel="Yes, save"
            onConfirm={() => void save()}
            onCancel={() => setConfirming(false)}
          />
        ) : (
          <button type="submit" className="btn primary sm" disabled={!changed || !value || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {saved && !changed && <span className="muted" style={{ fontSize: 12 }}>Saved</span>}
      </form>
      {note && <div className="muted" style={{ fontSize: 12 }}>{note}</div>}
      {error && <div className="field"><div className="err">{error}</div></div>}
    </div>
  );
}
